import React, { useState, useEffect } from "react";
import RoleToggle from "./_components/RoleToggle";
import Overview from "./_components/Overview";
import DriverMap from "./_components/DriverMap";
import GrafanaAnalytics from "./_components/GrafanaAnalytics";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { IconCalendarWeek } from "@tabler/icons-react";
import { useGrafanaStatus } from "../../apis/dashboardApis";

const tabs = ["Overview", "Live Tracking", "Grafana Analytics"];

export type SummaryPeriod =
  | "Today"
  | "Yesterday"
  | "Last 7 Days"
  | "Last 30 Days"
  | "All Time";

const FILTER_MAP = {
  Today: "Today",
  Yesterday: "Yesterday",
  Weekly: "Last 7 Days",
  Monthly: "Last 30 Days",
  All: "All Time",
} as const;

type UIFilter = keyof typeof FILTER_MAP;

const Dashboard: React.FC = () => {
  const profile = useSelector((state: any) => state.auth.profile);
  const [searchParams, setSearchParams] = useSearchParams();

  const tabFromURL = searchParams.get("tab") || "Overview";
  const [activeTab, setActiveTab] = useState(tabFromURL);
  const { data: isGrafanaRunning } = useGrafanaStatus();

  useEffect(() => {
    const currentTab = searchParams.get("tab") || "Overview";
    setActiveTab(currentTab);
  }, [searchParams]);

  useEffect(() => {
    if (activeTab === "Grafana Analytics" && isGrafanaRunning === false) {
      setSearchParams({ tab: "Overview" });
    }
  }, [activeTab, isGrafanaRunning, setSearchParams]);

  const [active, setActive] = useState<UIFilter>("All");
  const [customRange, setCustomRange] = useState<{
    startDate?: string;
    endDate?: string;
  }>({});

  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const buttons: UIFilter[] = [
    "All",
    "Today",
    "Yesterday",
    "Weekly",
    "Monthly",
  ];

  useEffect(() => {
    setSearchParams({ tab: activeTab });
  }, [activeTab]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 17) return "Good Afternoon";
    return "Good Evening";
  };

  const handlePresetClick = (label: UIFilter) => {
    setActive(label);
    setCustomRange({});
  };

  const handleApplyDate = () => {
    if (!startDate || !endDate) return;

    setActive(undefined as unknown as UIFilter);
    setCustomRange({ startDate, endDate });
    setOpen(false);
  };

  return (
    <div
      className={`pb-6 ${
        activeTab === "Grafana Analytics" ? "px-2 pt-0" : "px-6"
      }`}
    >
      {/* Top Bar for Overview only */}
      {activeTab === "Overview" && (
        <div className="flex justify-between items-center w-full pb-4 pt-2">
          <div>
            <p className="text-[#212121] text-base">
              Hello, {profile?.username || "Admin"},
            </p>
            <h1 className="text-3xl text-[#212121]">{getGreeting()}</h1>
          </div>

          <div className="flex justify-center relative">
            <div className="whitespace-nowrap overflow-x-auto no-scrollbar">
              <div className="inline-flex rounded-full bg-white border border-[#D8D8D8] py-0.5 px-1 space-x-2">
                {buttons.map((label) => (
                  <button
                    key={label}
                    onClick={() => handlePresetClick(label)}
                    className={`px-4 py-2 text-sm rounded-full transition-colors
                      ${
                        active === label
                          ? "bg-[#303030] text-white"
                          : "text-[#212121]/70 hover:bg-gray-100"
                      }`}
                  >
                    {label}
                  </button>
                ))}

                <div>
                  <button
                    className="flex px-4 py-2 text-sm rounded-full text-[#212121]/70"
                    onClick={() => setOpen(!open)}
                  >
                    <IconCalendarWeek size={18} className="mr-1" />
                    Select Date
                  </button>

                  {open && (
                    <div className="absolute mt-2 bg-white border shadow-lg rounded-xl p-4 z-50 w-72 right-0">
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">
                            Start Date
                          </label>
                          <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full border rounded-md px-3 py-2 text-sm"
                          />
                        </div>

                        <div>
                          <label className="block text-xs text-gray-500 mb-1">
                            End Date
                          </label>
                          <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full border rounded-md px-3 py-2 text-sm"
                          />
                        </div>

                        <button
                          onClick={handleApplyDate}
                          className="w-full bg-[#212121] text-white rounded-md py-2 text-sm"
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "Overview" && (
        <Overview
          summaryType={active ? FILTER_MAP[active] : undefined}
          startDate={customRange.startDate}
          endDate={customRange.endDate}
        />
      )}

      {activeTab === "Live Tracking" && (
        <div className="relative z-0 isolate">
          <DriverMap />
        </div>
      )}

      {activeTab === "Grafana Analytics" && isGrafanaRunning && (
        <GrafanaAnalytics />
      )}
    </div>
  );
};

export default Dashboard;

